/**
 * Settings Page
 *
 * Plugin configuration settings with auto-save, validation, and API integration.
 *
 * @package MultilingualPressZone
 * @since 1.0.0
 */

import { el, __, debounce, clear } from '../utils/dom.js';
import API from '../utils/api.js';
import { Tabs, TabPanel } from '../components/Tabs.js';
import { FormField } from '../components/Input.js';
import { Select } from '../components/Select.js';
import { Toggle } from '../components/Toggle.js';
import { Button } from '../components/Button.js';
import { StatusFeedback, SuccessCheckmark, ErrorIndicator } from '../components/StatusFeedback.js';
import Toast from '../components/Toast.js';
import Modal from '../components/Modal.js';
import logger from '../utils/logger.js';
import { PageHeader } from '../components/PageHeader.js';

/**
 * Settings Page Class
 */
export default class SettingsPage {
    /**
     * Initialize Settings Page
     *
     * @param {HTMLElement} container - Container element to render into
     */
    constructor(container, initialTab) {
        this.container = container;
        this.state = {
            // General Settings
            default_language: 'en',
            auto_translate: false,
            url_structure: 'subdirectory',
            url_prefix_wp_rules: true,
            character_limit: 10000,

            // Cache Settings
            enable_caching: true,
            cache_ttl: 3600,
            cache_stats: { hit_ratio: 0, size: 0, hits: 0, misses: 0 },

            // Performance
            query_optimization: true,
            prefetch_translations: false,
            batch_size: 50,

            // Advanced
            debug_mode: false,
            log_level: 'errors',
        };

        this.loading = false;
        this.saving = false;
        const validTabs = ['general', 'cache', 'performance', 'advanced'];
        this.activeTab = validTabs.includes(initialTab) ? initialTab : 'general';
        this.slideDirection = null;
        this.debouncedSave = debounce(this.saveSettings.bind(this), 1000);

        // Bind methods
        this.handleSettingChange = this.handleSettingChange.bind(this);
        this.render = this.render.bind(this);
    }

    /**
     * Initialize and render the page
     */
    async render() {
        try {
            this.loading = true;
            this.renderLoading();
            await this.loadSettings();
            this.loading = false;
            this.renderContent();
        } catch (error) {
            logger.error('Failed to load settings:', error);
            this.renderError(error.message);
        }
    }

    /**
     * Render loading state
     */
    renderLoading() {
        clear(this.container);
        this.container.appendChild(
            el('div', { class: 'presszone-multilingual-loading' },
                el('div', { class: 'presszone-multilingual-spinner' }),
                el('p', {}, __('Loading settings...', 'multilingual-press-zone'))
            )
        );
    }

    /**
     * Render error state
     *
     * @param {string} message - Error message
     */
    renderError(message) {
        clear(this.container);
        this.container.appendChild(
            el('div', { class: 'presszone-multilingual-error' },
                el('p', {}, __('Error loading settings:', 'multilingual-press-zone')),
                el('p', { class: 'error-message' }, message)
            )
        );
    }

    /**
     * Render main content
     */
    /**
     * Render main content
     */
    renderContent() {
        clear(this.container);

        const wrapper = el('div', { class: 'presszone-multilingual-settings' });

        const header = PageHeader({
            title: __('Translation Settings', 'multilingual-press-zone'),
            description: __('Configure translation behavior and API connection.', 'multilingual-press-zone'),
            variant: 'settings'
        });

        // Tabs
        const tabs = Tabs({
            tabs: [
                { id: 'general', label: __('General', 'multilingual-press-zone'), icon: '⚙️' },
                { id: 'cache', label: __('Cache', 'multilingual-press-zone'), icon: '⚡' },
                { id: 'performance', label: __('Performance', 'multilingual-press-zone'), icon: '🚀' },
                { id: 'advanced', label: __('Advanced', 'multilingual-press-zone'), icon: '🛠️' },
            ],
            activeTab: this.activeTab,
            className: 'presszone-multilingual-tabs',
            onChangeWithDirection: (tabId, direction) => {
                this.activeTab = tabId;
                this.slideDirection = direction;
                history.replaceState(null, '', `#/settings/${tabId}`);
                // Update tab button active states in the DOM
                const tablist = this.container.querySelector('[role="tablist"]');
                if (tablist) {
                    tablist.querySelectorAll('[role="tab"]').forEach(btn => {
                        const isActive = btn.dataset.tab === tabId;
                        btn.classList.toggle('presszone-multilingual-tabs__item--active', isActive);
                        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
                    });
                }
                this.renderTabContent();
            },
            onChange: () => { }
        });

        // Sync URL on initial render
        history.replaceState(null, '', `#/settings/${this.activeTab}`);

        // Tab Content Container
        const contentContainer = el('div', { class: 'presszone-multilingual-settings__content' });

        wrapper.appendChild(header);
        wrapper.appendChild(tabs);
        wrapper.appendChild(contentContainer);

        this.container.appendChild(wrapper);
        this.contentContainer = contentContainer;

        // Render initial tab
        this.renderTabContent();
    }

    /**
     * Render active tab content
     */
    renderTabContent() {
        if (!this.contentContainer) return;

        clear(this.contentContainer);

        let content;
        switch (this.activeTab) {
            case 'general':
                content = this.renderGeneralSettings();
                break;
            case 'api':
                content = this.renderApiSettings();
                break;
            case 'cache':
                content = this.renderCacheSettings();
                break;
            case 'performance':
                content = this.renderPerformanceSettings();
                break;
            case 'advanced':
                content = this.renderAdvancedSettings();
                break;
            default:
                content = el('p', {}, __('Unknown tab', 'multilingual-press-zone'));
        }

        this.contentContainer.appendChild(
            TabPanel({
                content,
                direction: this.slideDirection,
                className: 'presszone-multilingual-settings__panel-wrapper'
            })
        );

        this.slideDirection = null;
    }

    /**
     * Render General Settings
     */
    renderGeneralSettings() {
        const panel = el('div', { class: 'presszone-multilingual-settings__panel' });

        // General Settings Section
        panel.appendChild(
            el('h2', { class: 'presszone-multilingual-settings__section-title' },
                __('General Settings', 'multilingual-press-zone')
            )
        );

        // Default Language
        panel.appendChild(
            Select(
                'default_language',
                __('Default Language', 'multilingual-press-zone'),
                __('The default language for your site', 'multilingual-press-zone'),
                [
                    { value: 'en', label: __('English', 'multilingual-press-zone'), default: true },
                    { value: 'es', label: __('Spanish', 'multilingual-press-zone') },
                    { value: 'fr', label: __('French', 'multilingual-press-zone') },
                    { value: 'de', label: __('German', 'multilingual-press-zone') },
                    { value: 'it', label: __('Italian', 'multilingual-press-zone') },
                    { value: 'pt', label: __('Portuguese', 'multilingual-press-zone') },
                    { value: 'ja', label: __('Japanese', 'multilingual-press-zone') },
                    { value: 'zh', label: __('Chinese', 'multilingual-press-zone') },
                    { value: 'ar', label: __('Arabic', 'multilingual-press-zone') },
                    { value: 'ru', label: __('Russian', 'multilingual-press-zone') },
                ],
                this.state,
                this.handleSettingChange
            )
        );

        // Auto-translate on publish
        panel.appendChild(
            Toggle(
                'auto_translate',
                __('Auto-translate on Publish', 'multilingual-press-zone'),
                __('Automatically translate content when publishing', 'multilingual-press-zone'),
                this.state,
                this.handleSettingChange
            )
        );

        // URL Settings Section
        panel.appendChild(
            el('h2', { class: 'presszone-multilingual-settings__section-title' },
                __('URL Structure', 'multilingual-press-zone')
            )
        );

        // URL Structure
        panel.appendChild(
            Select(
                'url_structure',
                __('Language URL Structure', 'multilingual-press-zone'),
                __('How language is represented in URLs.', 'multilingual-press-zone'),
                [
                    { value: 'subdirectory', label: __('Subdirectory (/he/... )', 'multilingual-press-zone'), default: true },
                    { value: 'parameter', label: __('Query parameter (?lang=he)', 'multilingual-press-zone') },
                    { value: 'subdomain', label: __('Subdomain (he.example.com)', 'multilingual-press-zone') },
                ],
                this.state,
                this.handleSettingChange
            )
        );

        // Pretty URLs for posts and other content types
        panel.appendChild(
            Toggle(
                'url_prefix_wp_rules',
                __('Pretty URLs For Posts', 'multilingual-press-zone'),
                __('Enable /{lang}/... for posts, pages, and other content types (recommended for subdirectory mode).', 'multilingual-press-zone'),
                this.state,
                this.handleSettingChange
            )
        );

        // Service Settings
        panel.appendChild(
            el('h2', { class: 'presszone-multilingual-settings__section-title' },
                __('Service Settings', 'multilingual-press-zone')
            )
        );

        // Character Limit
        panel.appendChild(
            FormField(
                'character_limit',
                __('Character Limit per Translation', 'multilingual-press-zone'),
                __('Maximum characters to translate in a single request', 'multilingual-press-zone'),
                'number',
                this.state,
                this.handleSettingChange,
                { min: 1000, max: 100000, step: 1000 }
            )
        );

        return panel;
    }

    /**
     * Render API Configuration Settings
     */
    renderApiSettings() {
        const panel = el('div', { class: 'presszone-multilingual-settings__panel' });

        panel.appendChild(
            el('h2', { class: 'presszone-multilingual-settings__section-title' },
                __('API Configuration', 'multilingual-press-zone')
            )
        );

        // API URL
        panel.appendChild(
            FormField(
                'api_url',
                __('API URL', 'multilingual-press-zone'),
                __('Translation API endpoint', 'multilingual-press-zone'),
                'url',
                this.state,
                this.handleSettingChange,
                { placeholder: 'https://api.press.zone' }
            )
        );

        // API Key with show/hide toggle
        const apiKeyGroup = el('div', { class: 'presszone-multilingual-form-group' });
        const apiKeyInput = el('input', {
            type: 'password',
            id: 'api_key',
            class: 'presszone-multilingual-input--settings',
            value: this.state.api_key,
            oninput: (e) => {
                this.state.api_key = e.target.value;
                this.handleSettingChange('api_key', e.target.value);
            },
            placeholder: __('Enter your API key', 'multilingual-press-zone')
        });

        const toggleButton = Button({
            label: __('Show', 'multilingual-press-zone'),
            variant: 'ghost',
            size: 'sm',
            onClick: () => {
                const isPassword = apiKeyInput.type === 'password';
                apiKeyInput.type = isPassword ? 'text' : 'password';
                toggleButton.textContent = isPassword
                    ? __('Hide', 'multilingual-press-zone')
                    : __('Show', 'multilingual-press-zone');
            }
        });

        apiKeyGroup.appendChild(
            el('label', { class: 'presszone-multilingual-label--settings', for: 'api_key' },
                __('API Key', 'multilingual-press-zone'),
                el('span', { class: 'presszone-multilingual-tooltip', title: __('Your API authentication key', 'multilingual-press-zone') }, 'i')
            )
        );

        const inputWrapper = el('div', { class: 'api-key-wrapper' });
        inputWrapper.appendChild(apiKeyInput);
        inputWrapper.appendChild(toggleButton);
        apiKeyGroup.appendChild(inputWrapper);

        panel.appendChild(apiKeyGroup);

        // Test Connection Button and Status
        const testButton = Button({
            label: __('Test Connection', 'multilingual-press-zone'),
            variant: 'secondary',
            onClick: () => this.testConnection()
        });

        const statusIndicator = el('div', { class: 'connection-status' });
        this.updateConnectionStatus(statusIndicator);

        panel.appendChild(
            el('div', { class: 'api-actions' },
                testButton,
                statusIndicator
            )
        );

        return panel;
    }

    /**
     * Render Cache Settings
     */
    renderCacheSettings() {
        const panel = el('div', { class: 'presszone-multilingual-settings__panel' });

        panel.appendChild(
            el('h2', { class: 'presszone-multilingual-settings__section-title' },
                __('Cache Settings', 'multilingual-press-zone')
            )
        );

        // Enable Caching
        panel.appendChild(
            Toggle(
                'enable_caching',
                __('Enable Caching', 'multilingual-press-zone'),
                __('Cache translations to improve performance', 'multilingual-press-zone'),
                this.state,
                this.handleSettingChange
            )
        );

        // Cache TTL
        panel.appendChild(
            FormField(
                'cache_ttl',
                __('Cache TTL (seconds)', 'multilingual-press-zone'),
                __('How long to cache translations', 'multilingual-press-zone'),
                'number',
                this.state,
                this.handleSettingChange,
                { min: 60, max: 604800, step: 60 }
            )
        );

        // Cache Statistics
        const stats = this.state.cache_stats;
        panel.appendChild(
            el('div', { class: 'cache-stats' },
                el('h3', {}, __('Cache Statistics', 'multilingual-press-zone')),
                el('div', { class: 'stats-grid' },
                    el('div', { class: 'stat-item' },
                        el('span', { class: 'stat-label' }, __('Hit Ratio:', 'multilingual-press-zone')),
                        el('span', { class: 'stat-value' }, stats.hit_ratio + '%')
                    ),
                    el('div', { class: 'stat-item' },
                        el('span', { class: 'stat-label' }, __('Cache Size:', 'multilingual-press-zone')),
                        el('span', { class: 'stat-value' }, this.formatBytes(stats.size))
                    ),
                    el('div', { class: 'stat-item' },
                        el('span', { class: 'stat-label' }, __('Hits:', 'multilingual-press-zone')),
                        el('span', { class: 'stat-value' }, String(stats.hits))
                    ),
                    el('div', { class: 'stat-item' },
                        el('span', { class: 'stat-label' }, __('Misses:', 'multilingual-press-zone')),
                        el('span', { class: 'stat-value' }, String(stats.misses))
                    )
                )
            )
        );

        // Clear Cache Button
        panel.appendChild(
            Button({
                label: __('Clear Cache', 'multilingual-press-zone'),
                variant: 'danger',
                onClick: () => this.confirmClearCache()
            })
        );

        return panel;
    }

    /**
     * Render Performance Settings
     */
    renderPerformanceSettings() {
        const panel = el('div', { class: 'presszone-multilingual-settings__panel' });

        panel.appendChild(
            el('h2', { class: 'presszone-multilingual-settings__section-title' },
                __('Performance Settings', 'multilingual-press-zone')
            )
        );

        // Query Optimization (always on)
        panel.appendChild(
            Toggle(
                'query_optimization',
                __('Query Optimization', 'multilingual-press-zone'),
                __('Always active for optimal performance.', 'multilingual-press-zone'),
                this.state,
                null,
                false,
                { disabled: true }
            )
        );

        // Prefetch Translations
        panel.appendChild(
            Toggle(
                'prefetch_translations',
                __('Prefetch Translations', 'multilingual-press-zone'),
                __('Preload translations for better user experience', 'multilingual-press-zone'),
                this.state,
                this.handleSettingChange
            )
        );

        // Batch Size
        panel.appendChild(
            FormField(
                'batch_size',
                __('Batch Size', 'multilingual-press-zone'),
                __('Number of items to process in each batch', 'multilingual-press-zone'),
                'number',
                this.state,
                this.handleSettingChange,
                { min: 10, max: 100, step: 10 }
            )
        );

        return panel;
    }

    /**
     * Render Advanced Settings
     */
    renderAdvancedSettings() {
        const panel = el('div', { class: 'presszone-multilingual-settings__panel' });

        panel.appendChild(
            el('h2', { class: 'presszone-multilingual-settings__section-title' },
                __('Advanced Settings', 'multilingual-press-zone')
            )
        );

        // Debug Mode
        panel.appendChild(
            Toggle(
                'debug_mode',
                __('Debug Mode', 'multilingual-press-zone'),
                __('Enable verbose logging for troubleshooting', 'multilingual-press-zone'),
                this.state,
                this.handleSettingChange,
                true
            )
        );

        // Log Level
        panel.appendChild(
            Select(
                'log_level',
                __('Log Level', 'multilingual-press-zone'),
                __('What type of logs to record', 'multilingual-press-zone'),
                [
                    { value: 'none', label: __('None', 'multilingual-press-zone') },
                    { value: 'errors', label: __('Errors Only', 'multilingual-press-zone'), default: true },
                    { value: 'all', label: __('All Events', 'multilingual-press-zone') },
                ],
                this.state,
                this.handleSettingChange
            )
        );

        // Reset to Defaults
        panel.appendChild(
            el('div', { class: 'danger-zone' },
                el('h3', {}, __('Danger Zone', 'multilingual-press-zone')),
                el('p', { class: 'description' },
                    __('Resetting will restore all settings to their default values.', 'multilingual-press-zone')
                ),
                Button({
                    label: __('Reset to Defaults', 'multilingual-press-zone'),
                    variant: 'danger',
                    onClick: () => this.confirmResetDefaults()
                })
            )
        );

        return panel;
    }

    /**
     * Load settings from API
     */
    async loadSettings() {
        try {
            const response = await API.get('/settings');
            if (response.success && response.data) {
                // Support both flat and legacy nested response shapes.
                const d = response.data;
                const flat = d.general
                    ? {
                        default_language: d.general.default_language,
                        auto_translate: d.general.auto_translate_on_publish,
                        show_language_switcher: d.general.show_language_switcher,
                        url_structure: d.general.url_structure,
                        api_url: d.api?.api_url,
                        enable_caching: d.cache?.enabled,
                        cache_ttl: d.cache?.ttl,
                        query_optimization: d.performance?.query_optimization,
                        prefetch_translations: d.performance?.prefetch_translations,
                        batch_size: d.performance?.batch_size,
                        debug_mode: d.advanced?.debug_mode,
                    }
                    : d;
                Object.assign(this.state, flat);
            }
        } catch (error) {
            logger.error('Failed to load settings:', error);
            throw error;
        }
    }

    /**
     * Save settings to API
     *
     * @param {Object} data - Settings data to save
     */
    async saveSettings(data = null) {
        if (this.saving) return;

        try {
            this.saving = true;
            const settingsToSave = data || this.state;

            // Only send known, supported keys.
            const payload = {
                default_language: settingsToSave.default_language,
                auto_translate: settingsToSave.auto_translate,
                url_structure: settingsToSave.url_structure,
                url_prefix_wp_rules: settingsToSave.url_prefix_wp_rules,

                api_url: settingsToSave.api_url,
                api_key: settingsToSave.api_key,

                enable_caching: settingsToSave.enable_caching,
                cache_ttl: settingsToSave.cache_ttl,

                prefetch_translations: settingsToSave.prefetch_translations,
                batch_size: settingsToSave.batch_size,

                debug_mode: settingsToSave.debug_mode,
            };

            const response = await API.put('/settings', payload);

            if (response.success) {
                this.showToast(__('Settings saved successfully', 'multilingual-press-zone'), 'success');
            } else {
                throw new Error(response.message || __('Failed to save settings', 'multilingual-press-zone'));
            }
        } catch (error) {
            logger.error('Failed to save settings:', error);
            this.showToast(error.message, 'error');
        } finally {
            this.saving = false;
        }
    }

    /**
     * Handle setting change
     *
     * @param {string} key - Setting key
     * @param {*} value - New value
     */
    handleSettingChange(key, value) {
        this.state[key] = value;

        if (this.validateSetting(key, value)) {
            this.debouncedSave();
        }
    }

    /**
     * Validate a setting value
     *
     * @param {string} key - Setting key
     * @param {*} value - Value to validate
     * @returns {boolean} - Whether the value is valid
     */
    validateSetting(key, value) {
        switch (key) {
            case 'character_limit':
                if (value < 1000 || value > 100000) {
                    this.showToast(__('Character limit must be between 1,000 and 100,000', 'multilingual-press-zone'), 'error');
                    return false;
                }
                break;
            case 'cache_ttl':
                if (value < 60 || value > 604800) {
                    this.showToast(__('Cache TTL must be between 60 seconds and 7 days', 'multilingual-press-zone'), 'error');
                    return false;
                }
                break;
            case 'batch_size':
                if (value < 10 || value > 100) {
                    this.showToast(__('Batch size must be between 10 and 100', 'multilingual-press-zone'), 'error');
                    return false;
                }
                break;
            case 'api_url':
                if (!value || !value.startsWith('http')) {
                    this.showToast(__('API URL must be a valid HTTP/HTTPS URL', 'multilingual-press-zone'), 'error');
                    return false;
                }
                break;
        }
        return true;
    }

    /**
     * Test API connection
     */
    async testConnection() {
        try {
            this.showToast(__('Testing connection...', 'multilingual-press-zone'), 'info');

            const response = await API.post('/settings/test-connection', {
                api_url: this.state.api_url,
                api_key: this.state.api_key
            });

            if (response.success) {
                this.state.connection_status = 'connected';
                this.showToast(__('Connection successful!', 'multilingual-press-zone'), 'success');
            } else {
                this.state.connection_status = 'error';
                this.showToast(response.message || __('Connection failed', 'multilingual-press-zone'), 'error');
            }

            this.renderTabContent();
        } catch (error) {
            logger.error('Connection test failed:', error);
            this.state.connection_status = 'error';
            this.showToast(error.message, 'error');
            this.renderTabContent();
        }
    }

    /**
     * Clear cache with confirmation
     */
    confirmClearCache() {
        const modal = new Modal({
            title: __('Clear Cache?', 'multilingual-press-zone'),
            width: '400px'
        });

        const content = el('div', {},
            el('p', {},
                __('Are you sure you want to clear the translation cache? This action cannot be undone.', 'multilingual-press-zone')
            ),
            el('div', { class: 'modal-actions' },
                Button({
                    label: __('Cancel', 'multilingual-press-zone'),
                    variant: 'ghost',
                    onClick: () => modal.close()
                }),
                Button({
                    label: __('Clear Cache', 'multilingual-press-zone'),
                    variant: 'danger',
                    onClick: async () => {
                        await this.clearCache();
                        modal.close();
                    }
                })
            )
        );

        modal.open(content);
    }

    /**
     * Clear cache
     */
    async clearCache() {
        try {
            const response = await API.post('/settings/clear-cache');

            if (response.success) {
                this.state.cache_stats = { hit_ratio: 0, size: 0, hits: 0, misses: 0 };
                this.showToast(__('Cache cleared successfully', 'multilingual-press-zone'), 'success');
                this.renderTabContent();
            } else {
                throw new Error(response.message || __('Failed to clear cache', 'multilingual-press-zone'));
            }
        } catch (error) {
            logger.error('Failed to clear cache:', error);
            this.showToast(error.message, 'error');
        }
    }

    /**
     * Reset to defaults with confirmation
     */
    confirmResetDefaults() {
        const modal = new Modal({
            title: __('Reset to Defaults?', 'multilingual-press-zone'),
            width: '400px'
        });

        const content = el('div', {},
            el('p', {},
                __('Are you sure you want to reset all settings to their default values? This action cannot be undone.', 'multilingual-press-zone')
            ),
            el('div', { class: 'modal-actions' },
                Button({
                    label: __('Cancel', 'multilingual-press-zone'),
                    variant: 'ghost',
                    onClick: () => modal.close()
                }),
                Button({
                    label: __('Reset to Defaults', 'multilingual-press-zone'),
                    variant: 'danger',
                    onClick: async () => {
                        await this.resetDefaults();
                        modal.close();
                    }
                })
            )
        );

        modal.open(content);
    }

    /**
     * Reset settings to defaults
     */
    async resetDefaults() {
        try {
            const response = await API.post('/settings/reset');

            if (response.success && response.data) {
                Object.assign(this.state, response.data);
                this.showToast(__('Settings reset to defaults', 'multilingual-press-zone'), 'success');
                this.renderTabContent();
            } else {
                throw new Error(response.message || __('Failed to reset settings', 'multilingual-press-zone'));
            }
        } catch (error) {
            logger.error('Failed to reset settings:', error);
            this.showToast(error.message, 'error');
        }
    }

    /**
     * Update connection status indicator
     *
     * @param {HTMLElement} element - Status indicator element
     */
    updateConnectionStatus(element) {
        clear(element);

        let statusEl;
        switch (this.state.connection_status) {
            case 'connected':
                statusEl = el('span', { class: 'status-success' },
                    SuccessCheckmark(),
                    __('Connected', 'multilingual-press-zone')
                );
                break;
            case 'error':
                statusEl = el('span', { class: 'status-error' },
                    ErrorIndicator(),
                    __('Connection Failed', 'multilingual-press-zone')
                );
                break;
            default:
                statusEl = el('span', { class: 'status-unknown' },
                    __('Not Tested', 'multilingual-press-zone')
                );
        }

        element.appendChild(statusEl);
    }

    /**
     * Show toast notification
     *
     * @param {string} message - Message to display
     * @param {string} type - Toast type (success, error, info)
     */
    showToast(message, type = 'success') {
        Toast.show({
            message,
            type,
            duration: 3000
        });
    }

    /**
     * Format bytes to human-readable size
     *
     * @param {number} bytes - Size in bytes
     * @returns {string} - Formatted size
     */
    formatBytes(bytes) {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Cleanup and destroy
     */
    destroy() {
        if (this.container) {
            clear(this.container);
        }
        this.state = null;
        this.debouncedSave = null;
    }
}
